import React, { Fragment, useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { adminProductRequestAction } from '../redux/actions/productAction';
import { getAllCoupanAction } from '../redux/actions/coupanAction';
import { allCancelOrderByAdminAction, allReturnOrderByAdminAction } from '../redux/actions/orderAction';
import { allOrderAction } from '../redux/actions/orderAction';
import { usersRequestByAdmin } from '../redux/actions/userAction';

import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import './adminRoutes.css';

function AdminRoutes() {
  const dispatch = useDispatch();
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [revenueMetric, setRevenueMetric] = useState('gmv'); // 'gmv' | 'commission' | 'subscription'
  const [timeRange, setTimeRange] = useState('30d'); // 'today' | '7d' | '30d'
  const [marketplaceData, setMarketplaceData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const { products } = useSelector((state) => state.products);
  const { cancelOrders } = useSelector((state) => state.allCancelOrderByAdmin);
  const { returnOrders } = useSelector((state) => state.allReturnOrderByAdmin);
  const { orders } = useSelector((state) => state.allOrders);

  const fetchMarketplaceMetrics = async () => {
    try {
      setMetricsLoading(true);
      const { data } = await axios.get('/api/admin/marketplace/metrics');
      setMarketplaceData(data);
    } catch (err) {
      console.warn("Marketplace metrics API unavailable:", err.message);
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadDashboard = () => {
    dispatch(getAllCoupanAction());
    dispatch(allCancelOrderByAdminAction());
    dispatch(allReturnOrderByAdminAction());
    dispatch(allOrderAction());
    dispatch(adminProductRequestAction());
    dispatch(usersRequestByAdmin());
    fetchMarketplaceMetrics();
    setLastUpdated(new Date());
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const productList = useMemo(() => (Array.isArray(products) ? products : []), [products]);
  const orderList = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);
  const cancelOrderList = useMemo(() => (Array.isArray(cancelOrders) ? cancelOrders : []), [cancelOrders]);
  const returnOrderList = useMemo(() => (Array.isArray(returnOrders) ? returnOrders : []), [returnOrders]);

  const totalAmount = useMemo(() => {
    return orderList.reduce((sum, order) => sum + Number(order.totalPrice || 0), 0);
  }, [orderList]);

  const inrFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }),
    []
  );

  const lowStockProducts = productList.filter((product) => Number(product.productStock || 0) <= 5);
  const pendingOrders = orderList.filter((order) =>
    ['Processing', 'Confirmation-Request', 'Confirm'].includes(order.orderStatus)
  );

  // Fallback calculations if marketplaceData endpoint is pending
  const gmvValue = marketplaceData?.kpis?.totalGMV !== undefined ? marketplaceData.kpis.totalGMV : totalAmount;
  const commissionValue = marketplaceData?.kpis?.totalCommission !== undefined ? marketplaceData.kpis.totalCommission : 0;
  const subscriptionRev = marketplaceData?.kpis?.totalSubscriptionRevenue !== undefined ? marketplaceData.kpis.totalSubscriptionRevenue : 0;
  const activeSellersCount = marketplaceData?.kpis?.activeSellersCount !== undefined ? marketplaceData.kpis.activeSellersCount : 0;
  const pendingAppsCount = marketplaceData?.kpis?.pendingApplicationsCount !== undefined ? marketplaceData.kpis.pendingApplicationsCount : 0;
  const activeProdsCount = marketplaceData?.kpis?.activeProductsCount !== undefined ? marketplaceData.kpis.activeProductsCount : productList.length;

  // KPI summary cards
  const summaryCards = [
    {
      label: 'Marketplace GMV',
      value: inrFormatter.format(gmvValue),
      meta: `${orderList.length} total orders`,
      icon: <ShoppingBagOutlinedIcon />,
      to: '/Admin/dashboard/Orders',
      tone: 'neutral',
    },
    {
      label: 'Marketplace Commission',
      value: inrFormatter.format(commissionValue),
      meta: 'Tiered plan rates (0%–10%)',
      icon: <MonetizationOnOutlinedIcon />,
      to: '/Admin/dashboard/seller-plans',
      tone: 'brand',
    },
    {
      label: 'Subscription Revenue',
      value: inrFormatter.format(subscriptionRev),
      meta: 'Seller monthly plan fees',
      icon: <CardMembershipOutlinedIcon />,
      to: '/Admin/dashboard/seller-plans',
      tone: 'success',
    },
    {
      label: 'Active Sellers',
      value: activeSellersCount,
      meta: 'Approved vendors',
      icon: <StorefrontIcon />,
      to: '/Admin/dashboard/Vendors',
      tone: 'neutral',
    },
    {
      label: 'Pending Applications',
      value: pendingAppsCount,
      meta: pendingAppsCount > 0 ? 'Action required' : 'All clear',
      icon: <AssignmentTurnedInOutlinedIcon />,
      to: '/Admin/dashboard/seller-applications',
      tone: pendingAppsCount > 0 ? 'warning' : 'neutral',
      badge: pendingAppsCount > 0 ? 'Review' : null,
    },
    {
      label: 'Active Products',
      value: activeProdsCount,
      meta: `${lowStockProducts.length} low stock items`,
      icon: <Inventory2OutlinedIcon />,
      to: '/Admin/dashboard/Products',
      tone: 'neutral',
    },
  ];

  // Quick actions
  const quickActions = [
    { label: 'Review Applications', text: 'Approve or reject sellers', icon: <AssignmentTurnedInOutlinedIcon />, to: '/Admin/dashboard/seller-applications' },
    { label: 'Manage Seller Plans', text: 'Configure tiers & pricing', icon: <CardMembershipOutlinedIcon />, to: '/Admin/dashboard/seller-plans' },
    { label: 'Add Product', text: 'Create new catalogue item', icon: <AddCircleOutlineOutlinedIcon />, to: '/Admin/dashboard/Create/Product' },
    { label: 'Manage Coupons', text: 'Update promo offers', icon: <ConfirmationNumberOutlinedIcon />, to: '/Admin/dashboard/all/coupans' },
    { label: 'View Returns', text: `${returnOrderList.length} items to inspect`, icon: <AssignmentReturnOutlinedIcon />, to: '/Admin/dashboard/orders/return-orders' },
    { label: 'Delivery Forms', text: 'Customer fulfillment forms', icon: <ListAltOutlinedIcon />, to: '/Admin/dashboard/im-all/forms' },
  ];

  // Plan distribution data
  const defaultPlans = [
    { name: 'Basic', code: 'basic', price: 0, productLimit: 5, commissionRate: 10, sellerCount: 0, subscriptionRevenue: 0 },
    { name: 'Growth', code: 'growth', price: 300, productLimit: 20, commissionRate: 5, sellerCount: 0, subscriptionRevenue: 0 },
    { name: 'Pro', code: 'pro', price: 499, productLimit: 50, commissionRate: 0, sellerCount: 0, subscriptionRevenue: 0 },
  ];

  const planList = marketplaceData?.planDistribution?.length ? marketplaceData.planDistribution : defaultPlans;

  // Operational attention items
  const attentionItems = [
    { label: 'Pending seller applications', value: pendingAppsCount, severity: pendingAppsCount > 0 ? 'high' : 'normal', to: '/Admin/dashboard/seller-applications?status=pending' },
    { label: 'Sellers near/at product limit', value: marketplaceData?.capacityAlerts?.length || 0, severity: (marketplaceData?.capacityAlerts?.length || 0) > 0 ? 'medium' : 'normal', to: '/Admin/dashboard/Vendors' },
    { label: 'Low stock products (<=5)', value: lowStockProducts.length, severity: lowStockProducts.length > 0 ? 'medium' : 'normal', to: '/Admin/dashboard/Products?filter=low_stock' },
    { label: 'Pending order processing', value: pendingOrders.length, severity: pendingOrders.length > 0 ? 'normal' : 'normal', to: '/Admin/dashboard/Orders?status=Processing' },
    { label: 'Return requests awaiting review', value: returnOrderList.length, severity: returnOrderList.length > 0 ? 'normal' : 'normal', to: '/Admin/dashboard/orders/return-orders' },
    { label: 'Cancel requests', value: cancelOrderList.length, severity: cancelOrderList.length > 0 ? 'normal' : 'normal', to: '/Admin/dashboard/orders/allcancel' },
  ];

  return (
    <Fragment>
      <div className="dashboard-container">
        <Sidebar />
        <main className="main-container admin-dashboard-main">
          {/* ═══════════════════════ 1. HERO & CONTROLS ═══════════════════════ */}
          <section className="admin-dashboard-hero">
            <div>
              <span className="admin-dashboard-eyebrow">ADMIN CONTROL CENTER</span>
              <h1>Marketplace Overview</h1>
              <p>Monitor sellers, multi-vendor orders, product capacity, and platform revenue from one place.</p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div className="admin-time-range-pills" style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.1)', padding: '3px', borderRadius: '8px' }}>
                {[
                  { id: 'today', label: 'Today' },
                  { id: '7d', label: '7 Days' },
                  { id: '30d', label: '30 Days' },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTimeRange(t.id)}
                    style={{
                      background: timeRange === t.id ? '#ffffff' : 'transparent',
                      color: timeRange === t.id ? '#111315' : '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <button type="button" className="admin-refresh-button" onClick={loadDashboard} disabled={metricsLoading}>
                <RefreshOutlinedIcon className={metricsLoading ? 'spin-animation' : ''} />
                <span>{metricsLoading ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </section>

          {/* ═══════════════════════ 2. TOP MARKETPLACE KPIS ═══════════════════════ */}
          <section className="admin-kpi-grid" aria-label="Marketplace summary KPIs">
            {summaryCards.map((card) => (
              <Link className={`admin-kpi-card tone-${card.tone}`} to={card.to} key={card.label}>
                <span className="admin-kpi-icon">{card.icon}</span>
                <span className="admin-kpi-copy">
                  <small>{card.label}</small>
                  <strong>{card.value}</strong>
                  <em>{card.meta}</em>
                </span>
                <ArrowForwardIosOutlinedIcon className="admin-kpi-arrow" />
              </Link>
            ))}
          </section>

          {/* ═══════════════════════ 3. REVENUE STREAMS & SELLER GROWTH ═══════════════════════ */}
          <section className="admin-dashboard-grid-2col" style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '20px' }}>
            {/* Revenue Analytics Card */}
            <div className="admin-panel">
              <div className="admin-panel-heading">
                <div>
                  <h2>Marketplace Revenue Breakdown</h2>
                  <p>Distinguishing GMV, Commission, and Seller Subscriptions</p>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[
                    { id: 'gmv', label: 'GMV' },
                    { id: 'commission', label: 'Commission' },
                    { id: 'subscription', label: 'Subscriptions' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setRevenueMetric(m.id)}
                      style={{
                        background: revenueMetric === m.id ? '#111315' : '#f0f2f5',
                        color: revenueMetric === m.id ? '#ffffff' : '#4f5661',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '11.5px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: '#fbfaf8', padding: '14px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Gross Merchandise Value</div>
                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#111315', marginTop: '4px' }}>
                      {inrFormatter.format(gmvValue)}
                    </div>
                    <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700 }}>● Total customer sales</span>
                  </div>

                  <div style={{ background: '#fbfaf8', padding: '14px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Marketplace Commission</div>
                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#a85050', marginTop: '4px' }}>
                      {inrFormatter.format(commissionValue)}
                    </div>
                    <span style={{ fontSize: '11px', color: '#a85050', fontWeight: 700 }}>● Basic 10% / Growth 5%</span>
                  </div>

                  <div style={{ background: '#fbfaf8', padding: '14px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Subscription Revenue</div>
                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#16a34a', marginTop: '4px' }}>
                      {inrFormatter.format(subscriptionRev)}
                    </div>
                    <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700 }}>● Monthly seller plan fees</span>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #edf0f4', paddingTop: '14px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#4f5661', marginBottom: '8px' }}>
                    Commission Rate Model by Tier:
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#5a5854' }}>
                    <span><strong>Basic:</strong> ₹0 / 5 products (10% commission)</span>
                    <span><strong>Growth:</strong> ₹300 / 20 products (5% commission)</span>
                    <span><strong>Pro:</strong> ₹499 / 50 products (0% commission)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Seller Growth & Applications Snapshot */}
            <div className="admin-panel">
              <div className="admin-panel-heading">
                <div>
                  <h2>Seller Pipeline</h2>
                  <p>Onboarding and seller statuses</p>
                </div>
                <Link
                  to="/Admin/dashboard/seller-applications"
                  style={{
                    fontSize: '12px',
                    fontWeight: 800,
                    color: '#a85050',
                    textDecoration: 'none',
                  }}
                >
                  Manage All &rarr;
                </Link>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginTop: '16px' }}>
                <div style={{ background: '#fef3c7', padding: '14px', borderRadius: '10px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '11px', color: '#92400e', fontWeight: 800, textTransform: 'uppercase' }}>Pending Review</div>
                  <div style={{ fontSize: '26px', fontWeight: 900, color: '#92400e', margin: '4px 0' }}>
                    {pendingAppsCount}
                  </div>
                  <Link to="/Admin/dashboard/seller-applications" style={{ fontSize: '11.5px', fontWeight: 800, color: '#92400e', textDecoration: 'underline' }}>
                    Review Applications &rarr;
                  </Link>
                </div>

                <div style={{ background: '#dcfce7', padding: '14px', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '11px', color: '#166534', fontWeight: 800, textTransform: 'uppercase' }}>Active Sellers</div>
                  <div style={{ fontSize: '26px', fontWeight: 900, color: '#166534', margin: '4px 0' }}>
                    {activeSellersCount}
                  </div>
                  <span style={{ fontSize: '11.5px', color: '#166534', fontWeight: 700 }}>Approved &amp; selling</span>
                </div>
              </div>

              <div style={{ marginTop: '16px', background: '#fbfaf8', padding: '12px 14px', borderRadius: '8px', border: '1px solid #edf0f4' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#4f5661', marginBottom: '6px' }}>
                  Recent Application Pipeline:
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#5a5854' }}>
                  <span>Approved: <strong>{activeSellersCount}</strong></span>
                  <span>Pending: <strong>{pendingAppsCount}</strong></span>
                  <span>Rejected: <strong>{marketplaceData?.sellerApplications?.rejected || 0}</strong></span>
                </div>
              </div>
            </div>
          </section>

          {/* ═══════════════════════ 4. SELLER PLANS & CAPACITY ALERTS (2 COLUMNS) ═══════════════════════ */}
          <section className="admin-dashboard-grid-2col" style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Seller Plan Distribution */}
            <div className="admin-panel">
              <div className="admin-panel-heading">
                <div>
                  <h2>Seller Plan Distribution</h2>
                  <p>Active merchants enrolled across subscription tiers</p>
                </div>
                <Link to="/Admin/dashboard/seller-plans" style={{ fontSize: '12px', fontWeight: 800, color: '#a85050', textDecoration: 'none' }}>
                  Configure Plans &rarr;
                </Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                {planList.map((plan) => {
                  const isGrowth = plan.code === 'growth';
                  return (
                    <div
                      key={plan.code || plan.name}
                      style={{
                        background: '#fbfaf8',
                        border: isGrowth ? '1.5px solid #a85050' : '1px solid #edf0f4',
                        borderRadius: '10px',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ fontSize: '15px', color: '#111315' }}>{plan.name}</strong>
                          {isGrowth && (
                            <span style={{ fontSize: '9px', fontWeight: 900, background: '#a85050', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                              MOST POPULAR
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6a6864', marginTop: '2px' }}>
                          Limit: {plan.productLimit} items &middot; Commission: {plan.commissionRate}%
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#111315' }}>
                          {plan.price === 0 ? '₹0 Free' : `₹${plan.price}/mo`}
                        </div>
                        <span style={{ fontSize: '12px', color: '#737a84', fontWeight: 700 }}>
                          {plan.sellerCount !== undefined ? `${plan.sellerCount} sellers` : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Product Capacity Alerts */}
            <div className="admin-panel">
              <div className="admin-panel-heading">
                <div>
                  <h2>Product Capacity Alerts</h2>
                  <p>Sellers approaching or reaching plan listing limits</p>
                </div>
              </div>

              {marketplaceData?.capacityAlerts?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
                  {marketplaceData.capacityAlerts.map((alert, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: '#fbfaf8',
                        border: '1px solid #edf0f4',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <strong style={{ fontSize: '14px', color: '#111315' }}>{alert.storeName || alert.businessName}</strong>
                        <div style={{ fontSize: '12px', color: '#6a6864' }}>
                          Plan: {alert.planName} &middot; Usage: {alert.productCount} / {alert.productLimit} products ({alert.percentage}%)
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 800,
                          padding: '3px 8px',
                          borderRadius: '4px',
                          background: alert.status === 'Limit Reached' ? '#fee2e2' : '#fef3c7',
                          color: alert.status === 'Limit Reached' ? '#991b1b' : '#92400e',
                        }}
                      >
                        {alert.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '36px 12px', color: '#737a84' }}>
                  <CheckCircleOutlineIcon style={{ fontSize: '32px', color: '#16a34a', marginBottom: '6px' }} />
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315' }}>All Sellers Operating Within Limits</div>
                  <p style={{ fontSize: '12.5px', margin: '4px 0 0' }}>No active sellers have currently exceeded their tier product limits.</p>
                </div>
              )}
            </div>
          </section>

          {/* ═══════════════════════ 5. TOP SELLERS & OPERATIONAL ALERTS ═══════════════════════ */}
          <section className="admin-dashboard-grid-2col" style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '20px' }}>
            {/* Top Performing Sellers Table */}
            <div className="admin-panel">
              <div className="admin-panel-heading">
                <div>
                  <h2>Top Performing Sellers</h2>
                  <p>Ranked by gross sales volume and marketplace commission</p>
                </div>
                <Link to="/Admin/dashboard/Vendors" style={{ fontSize: '12px', fontWeight: 800, color: '#a85050', textDecoration: 'none' }}>
                  View All Sellers &rarr;
                </Link>
              </div>

              {marketplaceData?.topSellers?.length > 0 ? (
                <div style={{ overflowX: 'auto', marginTop: '14px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e0e4ea', textAlign: 'left', color: '#737a84' }}>
                        <th style={{ padding: '8px 6px', fontWeight: 800 }}>Seller / Store</th>
                        <th style={{ padding: '8px 6px', fontWeight: 800 }}>Plan</th>
                        <th style={{ padding: '8px 6px', fontWeight: 800 }}>Orders</th>
                        <th style={{ padding: '8px 6px', fontWeight: 800 }}>Sales</th>
                        <th style={{ padding: '8px 6px', fontWeight: 800 }}>Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketplaceData.topSellers.map((seller, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #edf0f4' }}>
                          <td style={{ padding: '10px 6px', fontWeight: 800 }}>
                            <Link to={`/Admin/vendors/${seller.sellerId}`} style={{ color: '#111315', textDecoration: 'underline' }}>
                              {seller.storeName || seller.businessName}
                            </Link>
                          </td>
                          <td style={{ padding: '10px 6px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 800, background: '#faedea', color: '#a85050', padding: '2px 6px', borderRadius: '4px' }}>
                              {seller.planName}
                            </span>
                          </td>
                          <td style={{ padding: '10px 6px', color: '#4f5661' }}>{seller.ordersCount}</td>
                          <td style={{ padding: '10px 6px', fontWeight: 800, color: '#111315' }}>
                            {inrFormatter.format(seller.totalSales)}
                          </td>
                          <td style={{ padding: '10px 6px', fontWeight: 800, color: '#16a34a' }}>
                            {inrFormatter.format(seller.commissionEarned)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '36px 12px', color: '#737a84' }}>
                  <StorefrontIcon style={{ fontSize: '32px', color: '#c0c5cc', marginBottom: '6px' }} />
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315' }}>Seller Sales Aggregation Ready</div>
                  <p style={{ fontSize: '12.5px', margin: '4px 0 0' }}>Seller order performance will populate as multi-vendor orders are placed.</p>
                </div>
              )}
            </div>

            {/* Needs Attention Panel */}
            <div className="admin-panel">
              <div className="admin-panel-heading">
                <div>
                  <h2>Needs Attention</h2>
                  <p>Marketplace queues and operational alerts</p>
                </div>
                <span className="admin-last-updated">
                  {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              <div className="admin-attention-list" style={{ marginTop: '14px' }}>
                {attentionItems.map((item) => (
                  <Link to={item.to} className={`admin-attention-row severity-${item.severity}`} key={item.label}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {item.severity === 'high' && <WarningAmberOutlinedIcon style={{ fontSize: '16px', color: '#dc2626' }} />}
                      <span>{item.label}</span>
                    </div>
                    <strong>{item.value}</strong>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* ═══════════════════════ 6. RECENT MARKETPLACE ORDERS ═══════════════════════ */}
          <section className="admin-panel" style={{ marginTop: '20px' }}>
            <div className="admin-panel-heading">
              <div>
                <h2>Recent Marketplace Orders</h2>
                <p>Latest multi-vendor customer checkouts and commission calculations</p>
              </div>
              <Link to="/Admin/dashboard/Orders" style={{ fontSize: '12px', fontWeight: 800, color: '#a85050', textDecoration: 'none' }}>
                View All Orders ({orderList.length}) &rarr;
              </Link>
            </div>

            {marketplaceData?.recentOrders?.length > 0 ? (
              <div style={{ overflowX: 'auto', marginTop: '14px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e0e4ea', textAlign: 'left', color: '#737a84' }}>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Order ID</th>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Customer</th>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Seller / Store</th>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Items</th>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Order Value</th>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Commission</th>
                      <th style={{ padding: '8px 6px', fontWeight: 800 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marketplaceData.recentOrders.map((ord) => (
                      <tr key={ord._id} style={{ borderBottom: '1px solid #edf0f4' }}>
                        <td style={{ padding: '10px 6px', fontWeight: 800, color: '#111315', fontFamily: 'monospace' }}>
                          #{ord._id.slice(-6).toUpperCase()}
                        </td>
                        <td style={{ padding: '10px 6px', color: '#111315' }}>{ord.customer}</td>
                        <td style={{ padding: '10px 6px', color: '#4f5661' }}>{ord.sellerName}</td>
                        <td style={{ padding: '10px 6px' }}>{ord.itemCount}</td>
                        <td style={{ padding: '10px 6px', fontWeight: 800, color: '#111315' }}>
                          {inrFormatter.format(ord.totalPrice)}
                        </td>
                        <td style={{ padding: '10px 6px', fontWeight: 800, color: '#16a34a' }}>
                          {ord.commission ? inrFormatter.format(ord.commission) : '—'}
                        </td>
                        <td style={{ padding: '10px 6px' }}>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 800,
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: ord.orderStatus === 'Delivered' ? '#dcfce7' : '#fef3c7',
                              color: ord.orderStatus === 'Delivered' ? '#166534' : '#92400e',
                            }}
                          >
                            {ord.orderStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '36px 12px', color: '#737a84' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315' }}>No Orders Recorded Yet</div>
              </div>
            )}
          </section>

          {/* ═══════════════════════ 7. MARKETPLACE QUICK ACTIONS ═══════════════════════ */}
          <section className="admin-panel admin-actions-panel" style={{ marginTop: '20px' }}>
            <div className="admin-panel-heading">
              <div>
                <h2>Marketplace Quick Actions</h2>
                <p>Direct shortcuts for routine marketplace management workflows</p>
              </div>
            </div>
            <div className="admin-quick-actions" style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              {quickActions.map((action) => (
                <Link className="admin-quick-action" to={action.to} key={action.label}>
                  <span>{action.icon}</span>
                  <strong>{action.label}</strong>
                  <small>{action.text}</small>
                </Link>
              ))}
            </div>
          </section>
        </main>
      </div>
    </Fragment>
  );
}

export default AdminRoutes;
